import {
  AuthTokenErrorType,
  AuthTokenSource,
  IAuthTokenData,
  IAuthTokenError,
  IAuthTokenFactory,
  IAuthTokenOptions,
  IAuthTokenStore,
  ISerializedAuthToken,
  ISourceTokenDebugger,
} from './authTokenTypes';
import { action, makeAutoObservable } from 'mobx';
import { AuthTokenHelper } from './authTokenHelper';
import { IWallClock } from '../../utils/time/IWallClock';
import { DefaultWallClock } from '../../utils/time/DefaultWallClock';
import { SourceTokenDebugger } from './TokenDebugger';
import {
  ServerCookies,
  WriteableServerCookies,
} from '../../utils/serverCookies';
import { clientConfig } from '../../config';

export const BW_SESSIONID_COOKIE_NAME = 'BW_SESSIONID';
export const BW_ISLOGGEDIN_COOKIE_NAME = 'bruntwoodSession.isLoggedIn';

const config = clientConfig();

export class BruntwoodSessionStore implements IAuthTokenStore {
  public readonly source = 'bruntwoodSession';
  public readonly expiryDate = null;

  public readonly isRequired = false;
  /**
   * WE have no way of getting the expiry date of the bruntwood session token
   */
  public readonly isExpired = false;

  public readonly subscriber = null;

  public readonly shouldRefresh = false;

  public readonly usedCookieNames: string[] = [
    BW_SESSIONID_COOKIE_NAME,
    BW_ISLOGGEDIN_COOKIE_NAME,
  ];

  /**
   * Will be used to figure out if this session is actually a logged in session or a visitor session.
   * @private
   */
  private _isLoggedIn: boolean = false;

  /**
   * We can't get one of these anywhere other than logging in
   */
  public readonly dependencies: AuthTokenSource[] = [];

  private _value: string | null;

  public readonly timeoutExpiry = null;

  private readonly helper: AuthTokenHelper;

  constructor(
    value: string | null,
    isLoggedIn: boolean,
    wallClock: IWallClock = DefaultWallClock.instance,
    private readonly tokenDebugger: ISourceTokenDebugger = new SourceTokenDebugger(
      this.source,
    ),
  ) {
    this._value = value;
    this._isLoggedIn = isLoggedIn;
    this.helper = new AuthTokenHelper({
      wallClock,
    });
    makeAutoObservable(this);
  }

  get meta(): Record<string, string | null> {
    return {
      isLoggedIn: this._isLoggedIn ? 'true' : null,
    };
  }

  get value(): string | null {
    return this._value;
  }

  get isReady(): boolean {
    return (
      !!this._value &&
      !this.helper.isInvalidatedByFailures() &&
      this._isLoggedIn
    );
  }

  public readonly canAttemptAgain = false;

  saveCookies(writeableCookies: WriteableServerCookies): void {
    // BW_SESSIONID is deliberately NOT written here (BCD-1794). companion-auth is the only writer of
    // the session cookie: it sets it with a max-age on every response, and this store only ever
    // held a copy of the value the browser already sent us. Echoing it back without attributes
    // replaced the persistent cookie with a session-only one on every page render. Logout still
    // clears it via usedCookieNames.

    if (this._isLoggedIn) {
      writeableCookies.set(BW_ISLOGGEDIN_COOKIE_NAME, 'true');
    } else {
      writeableCookies.clear(BW_ISLOGGEDIN_COOKIE_NAME);
    }
  }

  async checkResponse(response: Response): Promise<IAuthTokenError | null> {
    if (response.status === 401) {
      this.helper.recordFailedRequest();
      this.tokenDebugger.addEvent({
        action: 'fetchFailed',
      });
      return {
        source: this.source,
        type: AuthTokenErrorType.UNAUTHORIZED,
        message: 'Unauthorized',
      };
    }

    return null;
  }

  @action
  update(data: IAuthTokenData): void {
    this.tokenDebugger.addEvent({
      action: 'updated',
    });
    this._value = data.value;
  }

  updateWith(): Promise<IAuthTokenError | null> {
    return Promise.resolve(null);
  }

  async logoutCallback(): Promise<void> {
    try {
      if (this._value) {
        await fetch(`${config.authServiceUrl}/api/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Cookie: `${BW_SESSIONID_COOKIE_NAME}=${this.value}; ${BW_ISLOGGEDIN_COOKIE_NAME}=${this._isLoggedIn};`,
          },
        });
        this.tokenDebugger.addEvent({
          action: 'logoutSucceeded',
        });
      }
    } catch (error: any) {
      this.tokenDebugger.addEvent({
        action: 'logoutFailed',
        message: error.message,
      });
    }
  }
}

export class BruntwoodSessionStoreFactory implements IAuthTokenFactory {
  createEmpty(options?: IAuthTokenOptions): IAuthTokenStore {
    const tokenDebugger =
      options?.debugger?.forSource('bruntwoodSession') ||
      new SourceTokenDebugger('bruntwoodSession');
    tokenDebugger.addEvent({
      action: 'createdEmpty',
      message: 'Created empty BruntwoodSessionStore',
    });

    return new BruntwoodSessionStore(
      null,
      false,
      options?.wallClock,
      tokenDebugger,
    );
  }

  createOnClient(
    fromServer: ISerializedAuthToken,
    options?: IAuthTokenOptions,
  ): IAuthTokenStore {
    const tokenDebugger =
      options?.debugger?.forSource('bruntwoodSession') ||
      new SourceTokenDebugger('bruntwoodSession');

    return new BruntwoodSessionStore(
      fromServer.token,
      !!fromServer.meta.isLoggedIn,
      options?.wallClock,
      tokenDebugger,
    );
  }

  createOnServer(
    cookies: ServerCookies,
    options?: IAuthTokenOptions,
  ): IAuthTokenStore | undefined {
    const tokenDebugger =
      options?.debugger?.forSource('bruntwoodSession') ||
      new SourceTokenDebugger('bruntwoodSession');

    const value = cookies.getValueOf(BW_SESSIONID_COOKIE_NAME);
    const isLoggedIn = cookies.getValueOf(BW_ISLOGGEDIN_COOKIE_NAME);

    if (value) {
      tokenDebugger.addEvent({
        action: 'loadedFromCookie',
        message: 'Loaded from cookie',
      });
    } else {
      tokenDebugger.addEvent({
        action: 'createdEmpty',
        message: 'Created empty BruntwoodSessionStore',
      });
    }

    return new BruntwoodSessionStore(
      value || null,
      isLoggedIn === 'true',
      options?.wallClock,
      tokenDebugger,
    );
  }
}
